import * as React from 'react';
import useStyles from './use_styles';
import useControlled from '../utils/use_controlled';
import { RadioGroupInterface } from './types';
import highContrastMediaQuery from '../utils/high_contrast_media_query';
import { useTheme } from '@veneer/theme';
import { RadioButtonProps } from '../radio_button/types';

import { warnDisabledConflict } from '../utils/warnDisabledConflict';

let warned = false;

const RadioGroup = React.forwardRef<
  HTMLFieldSetElement,
  RadioGroupInterface &
    Omit<React.HTMLAttributes<HTMLFieldSetElement>, keyof RadioGroupInterface>
>(
  (
    {
      alignment = 'vertical',
      'aria-disabled': ariaDisabled,
      children,
      className,
      disabled: disabledProp = false,
      defaultValue,
      name,
      label,
      onChange,
      value: valueProp,
      ...props
    },
    ref
  ) => {
    warned = warnDisabledConflict(disabledProp, ariaDisabled, warned);

    const [value, setValue] = useControlled({
      controlled: valueProp,
      default: defaultValue,
    });
    const theme = useTheme();

    const handleChange = React.useCallback(
      (event) => {
        setValue(event.target.value);

        onChange?.(event, event.target.value);
      },
      [onChange, setValue]
    );

    const preparedChildren = React.useMemo(() => {
      const childrenArray: React.ReactNode[] = [];

      React.Children.forEach(children, (child, index) => {
        if (React.isValidElement(child)) {
          const checked =
            typeof value === 'undefined' ? false : child.props.value === value;
          const clonedProps: RadioButtonProps = {
            'aria-disabled':
              ariaDisabled === true ||
              ariaDisabled === 'true' ||
              child.props['aria-disabled'],
            disabled: disabledProp || child.props.disabled,
            checked,
            name,
            onChange: handleChange,
            key: `${name}-${index}`,
          };
          const clonedChild = React.cloneElement(child, clonedProps);
          childrenArray.push(clonedChild);
        }
      });

      return childrenArray;
    }, [ariaDisabled, children, disabledProp, handleChange, name, value]);

    const style = useStyles();
    const disabled = disabledProp || ariaDisabled;

    return (
      <fieldset
        {...props}
        disabled={disabledProp}
        aria-disabled={ariaDisabled}
        className={className}
        css={[
          style.root,
          alignment === 'vertical' && style.vertical,
          highContrastMediaQuery(theme),
        ]}
        ref={ref}
      >
        {label && (
          <legend
            className="label vn-radio-buttons__span-text"
            css={[style.legend.base, disabled && style.legend.disabled]}
          >
            {label}
          </legend>
        )}
        {preparedChildren}
      </fieldset>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
